import {combine, guard, merge, sample} from 'effector';
import {createGate} from 'effector-react';
import i18next from 'i18next';

import {toast} from 'react-toastify';

import {rootDomain} from '@/app/model';
import {showRestError} from '@app/errors';
import {SummaryGate} from '@entities/summary/model';
import {userModel} from '@entities/user/model';

import {TAXPAYER_ROLES} from '@entities/user/permissions';
import {UserRole} from '@entities/user/types';

import * as api from './api';
import {SubscriptionStatus, SubscriptionTariff} from './types';

const domain = rootDomain.createDomain('Subscriptions');

export const SubscriptionsGate = createGate<{isSubscriptionsPage?: boolean}>('Subscriptions');

export const fetchSubscriptionFx = domain.createEffect(api.fetchSubscription);
export const $subscriptionStatus = domain
    .createStore<SubscriptionStatus | null>(null)
    .reset(userModel.state);

export const fetchTariffsFx = domain.createEffect(api.fetchTariffs);
export const $tariffs = domain.createStore<SubscriptionTariff[] | null>(null);

export const fetchPaymentInvoiceFx = domain.createEffect(api.fetchPaymentInvoice);
export const doFetchPaymentInvoice = domain.createEvent();
sample({
    clock: doFetchPaymentInvoice,
    source: userModel.state.map((s) => s.profile?.company.tin ?? ''),
    target: fetchPaymentInvoiceFx,
});

const allowByRoleGuard = guard({
    clock: SubscriptionsGate.open,
    source: userModel.state.map((s) => s.role),
    filter: (role: UserRole | null) => Boolean(role && TAXPAYER_ROLES.includes(role)),
});

export const $invocieBillingBinaryPdf = domain.createStore<string | ArrayBuffer | null>(null);
export const setInvocieBillingBinaryPdf = domain.createEvent<string | ArrayBuffer | null>();
$invocieBillingBinaryPdf.on(setInvocieBillingBinaryPdf, (_, data) => data);

guard({
    clock: allowByRoleGuard,
    source: SubscriptionsGate.state,
    filter: ({isSubscriptionsPage}: {isSubscriptionsPage?: boolean}) => !!isSubscriptionsPage,
    target: fetchTariffsFx,
});
sample({
    clock: fetchTariffsFx.doneData,
    target: $tariffs,
});

export const activateTrialFx = domain.createEffect(api.activateTrial);
export const activateTrial = domain.createEvent();
sample({
    clock: activateTrial,
    source: userModel.state.map((s) => s.profile?.company.tin ?? ''),
    target: activateTrialFx,
});

export const paySubscriptionFx = domain.createEffect(api.paySubscription);
export const paySubscription = domain.createEvent<string>('');
sample({
    clock: paySubscription,
    source: userModel.state.map((s) => s.profile?.company.tin ?? ''),
    fn: (tin, tariffId) => ({tin, tariffId}),
    target: paySubscriptionFx,
});
$invocieBillingBinaryPdf.on(
    [paySubscriptionFx.doneData, fetchPaymentInvoiceFx.doneData],
    (_, data) => data,
);

export const cancelSubscriptionFx = domain.createEffect(api.cancelSubscription);
export const cancelSubscription = domain.createEvent();
sample({
    clock: cancelSubscription,
    source: userModel.state.map((s) => s.profile?.company.tin ?? ''),
    target: cancelSubscriptionFx,
});

sample({
    clock: merge([
        activateTrialFx.doneData,
        allowByRoleGuard,
        paySubscriptionFx.doneData,
        cancelSubscriptionFx.doneData,
    ]),
    source: userModel.state.map((s) => s.profile?.company.tin ?? ''),
    target: fetchSubscriptionFx,
});

$subscriptionStatus.on(fetchSubscriptionFx.doneData, (_, data) => data).reset(SummaryGate.close);

export const $subscriptionPaymentPending = combine(
    cancelSubscriptionFx.pending,
    fetchPaymentInvoiceFx.pending,
    (cancelSubscriptionFx, fetchPaymentInvoiceFx) => cancelSubscriptionFx || fetchPaymentInvoiceFx,
);
export const $subscriptionPending = combine(
    fetchSubscriptionFx.pending,
    activateTrialFx.pending,
    paySubscriptionFx.pending,
    $subscriptionPaymentPending,
    (fetchSubscriptionFx, activateTrialFx, paySubscriptionFx, $subscriptionPaymentPending) =>
        fetchSubscriptionFx || activateTrialFx || paySubscriptionFx || $subscriptionPaymentPending,
);

activateTrialFx.doneData.watch(() =>
    toast.success(i18next.t('subscriptions.activateTrial.messages.success')),
);
cancelSubscriptionFx.doneData.watch(() =>
    toast.success(i18next.t('subscriptions.cancelSubscription.messages.success')),
);

paySubscriptionFx.failData.watch(showRestError);
fetchTariffsFx.failData.watch(showRestError);
activateTrialFx.failData.watch(showRestError);
cancelSubscriptionFx.failData.watch(showRestError);
fetchSubscriptionFx.failData.watch(showRestError);
